/* eslint-disable no-console */
import React, {
  createContext,
  createRef,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { noop } from '@highlight-ui/utils-commons';

import {
  MIN_OPTIONS_TO_DISPLAY_SELECT_ALL,
  searchSubstring,
} from '../../helpers';
import { SelectOption, SelectOptionType, SelectVariant } from '../../types';
import { SubmitFooterHandle } from '../../Footer/SubmitFooter';

type SelectContentProps = {
  multiple: boolean;
  closeOnSelect: boolean;
  isClearable: boolean;
  enableSubLabelSearch: boolean;
  isLoading: boolean;
  isVisible: boolean;
  showSelectAll: boolean;
  setIsVisible: (data: boolean) => void;
  searchInputValue: string;
  setSearchInputValue: React.Dispatch<React.SetStateAction<string>>;
  options: SelectOption[];
  setOptions: React.Dispatch<React.SetStateAction<SelectOption[]>>;
  hasFooter: boolean;
  setHasFooter: React.Dispatch<React.SetStateAction<boolean>>;
  hasSearch: boolean;
  setHasSearch: React.Dispatch<React.SetStateAction<boolean>>;
  optionsRef: React.RefObject<HTMLLIElement>[];
  setOptionsRef: React.Dispatch<
    React.SetStateAction<React.RefObject<HTMLLIElement>[]>
  >;
  selectedOptions: SelectOption[];
  selectedOptionsWithValuesOnly?: (string | number)[];
  filteredOptions: SelectOption[];
  shouldShowSearchInput: boolean;
  selectAllIsSelected: boolean;
  shouldShowNoResultsLabel: boolean;
  isFullWidthVariant: boolean;
  optionType: SelectOptionType;
  onSelect: (e: SelectOption[]) => void;
  onSearchChange: (e: string) => void;
  submitRef: React.MutableRefObject<SubmitFooterHandle | null>;
  focusedOptionRef: React.MutableRefObject<number | null>;
  resetSearchOnClose: boolean;
};

export const SelectContext = createContext<SelectContentProps>({
  multiple: false,
  closeOnSelect: false,
  isClearable: false,
  enableSubLabelSearch: false,
  isLoading: false,
  isVisible: false,
  showSelectAll: false,
  setIsVisible: console.error,
  searchInputValue: '',
  setSearchInputValue: console.error,
  options: [],
  setOptions: console.error,
  hasFooter: false,
  setHasFooter: console.error,
  hasSearch: false,
  setHasSearch: console.error,
  optionsRef: [],
  setOptionsRef: console.error,
  selectedOptions: [],
  filteredOptions: [],
  selectedOptionsWithValuesOnly: [],
  shouldShowSearchInput: false,
  selectAllIsSelected: false,
  shouldShowNoResultsLabel: false,
  isFullWidthVariant: false,
  optionType: 'single-line',
  onSelect: console.error,
  onSearchChange: console.error,
  submitRef: { current: null },
  focusedOptionRef: { current: null },
  resetSearchOnClose: false,
});

type SelectProviderProps = {
  children: ReactNode;
  selectedOptions?: SelectOption[];
  minOptionsToDisplaySearch?: number;
  variant?: SelectVariant;
  optionType: SelectOptionType;
  onSelect: (e: SelectOption[]) => void;
  multiple?: boolean;
  closeOnSelect?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  searchInitialValue?: string;
  onSearchChange?: (e: string) => void;
  enableSubLabelSearch?: boolean;
  resetSearchOnClose?: boolean;
};

export function SelectProvider({
  children,
  selectedOptions = [],
  minOptionsToDisplaySearch = 8,
  variant = 'inline',
  optionType = 'single-line',
  onSelect = noop,
  multiple = false,
  closeOnSelect = false,
  isClearable = false,
  isLoading = false,
  searchInitialValue = '',
  onSearchChange = noop,
  enableSubLabelSearch = false,
  resetSearchOnClose = false,
}: SelectProviderProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [hasFooter, setHasFooter] = useState<boolean>(false);
  const [hasSearch, setHasSearch] = useState<boolean>(false);
  const [showSelectAll, setShowSelectAll] = useState<boolean>(false);
  const [searchInputValue, setSearchInputValue] =
    useState<string>(searchInitialValue);
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [optionsRef, setOptionsRef] = useState<
    React.RefObject<HTMLLIElement>[]
  >([]);

  const focusedOptionRef = useRef<number | null>(null);
  const submitRef = useRef<SubmitFooterHandle | null>(null);

  const selectedOptionsWithValuesOnly = useMemo(
    () => selectedOptions?.map((selectedOption) => selectedOption.value) || [],
    [selectedOptions],
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          searchSubstring(option?.label, searchInputValue) !== -1 ||
          (enableSubLabelSearch &&
            option.subLabel &&
            searchSubstring(option.subLabel, searchInputValue) !== -1),
      ),
    [options, searchInputValue, enableSubLabelSearch],
  );

  useEffect(() => {
    setShowSelectAll(
      multiple &&
        searchInputValue?.length === 0 &&
        options?.length > MIN_OPTIONS_TO_DISPLAY_SELECT_ALL,
    );
  }, [multiple, onSearchChange, options?.length, searchInputValue]);

  useEffect(() => {
    setOptionsRef((prevState) =>
      Array(filteredOptions.length + (showSelectAll ? 1 : 0))
        .fill(0)
        .map((_, i) => prevState[i] || createRef()),
    );
  }, [
    filteredOptions,
    multiple,
    showSelectAll,
    options?.length,
    searchInputValue?.length,
  ]);

  useEffect(() => {
    onSearchChange(searchInputValue);
  }, [onSearchChange, searchInputValue]);

  useEffect(() => {
    if (!isVisible && focusedOptionRef.current !== null) {
      focusedOptionRef.current = null;
    }
    if (!isVisible && resetSearchOnClose) {
      setSearchInputValue(searchInitialValue);
    }
  }, [isVisible, resetSearchOnClose, searchInitialValue]);

  const value = useMemo(
    () => ({
      optionType,
      isVisible,
      setIsVisible,
      multiple,
      closeOnSelect,
      isClearable,
      enableSubLabelSearch,
      isLoading,
      focusedOptionRef,
      submitRef,
      hasFooter,
      setHasFooter,
      hasSearch,
      setHasSearch,
      searchInputValue,
      showSelectAll,
      setSearchInputValue,
      options,
      setOptions,
      optionsRef,
      setOptionsRef,
      selectedOptions,
      filteredOptions,
      selectedOptionsWithValuesOnly,
      shouldShowSearchInput: options.length >= minOptionsToDisplaySearch,
      selectAllIsSelected:
        options.length === selectedOptionsWithValuesOnly.length,
      shouldShowNoResultsLabel:
        searchInputValue !== '' && filteredOptions.length === 0,
      isFullWidthVariant: variant === 'full-width',
      onSelect,
      onSearchChange,
      resetSearchOnClose,
    }),
    [
      optionType,
      isVisible,
      multiple,
      closeOnSelect,
      isClearable,
      enableSubLabelSearch,
      isLoading,
      hasFooter,
      hasSearch,
      searchInputValue,
      showSelectAll,
      options,
      optionsRef,
      selectedOptions,
      filteredOptions,
      selectedOptionsWithValuesOnly,
      minOptionsToDisplaySearch,
      variant,
      onSelect,
      onSearchChange,
      resetSearchOnClose,
    ],
  );

  return (
    <SelectContext.Provider value={value}>{children}</SelectContext.Provider>
  );
}
